package fr.frux.feature.support

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import fr.frux.shared.domain.model.SupportPrompt
import fr.frux.shared.domain.repository.SupportRepository
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

data class SupportPromptUiState(
    val prompt: SupportPrompt? = null,
)

/**
 * Sollicitation de soutien (spec 2026-08-13).
 *
 * Le ViewModel est permanent (composé par `App`, hors du `NavDisplay`) : l'état vit ici et non dans
 * un effet d'écran, que la recomposition de l'`entryProvider` annulerait.
 *
 * Une seule interrogation par session. Le serveur porte le capping durable (30 jours). Ce drapeau
 * évite seulement de le rappeler à chaque aller-retour vers la carte.
 */
class SupportPromptViewModel(
    private val supportRepository: SupportRepository,
    private val donationsAllowed: Boolean,
) : ViewModel() {
    private val _state = MutableStateFlow(SupportPromptUiState())
    val state: StateFlow<SupportPromptUiState> = _state.asStateFlow()

    private var asked = false

    fun onMapBecameVisible() {
        // Pas même d'appel sur Android et iOS (#473) : l'invite ne dépend pas du seul
        // SUPPORT_DISABLED_PLATFORMS serveur, qu'une configuration oubliée rallumerait.
        if (asked || !donationsAllowed) return
        asked = true
        viewModelScope.launch {
            // Un échec réseau n'affiche rien et ne se rejoue pas. Ni prompt fantôme, ni double
            // sollicitation.
            val prompt = supportRepository.prompt().getOrNull() ?: return@launch
            _state.update { it.copy(prompt = prompt) }
            supportRepository.markShown()
        }
    }

    fun onDismiss() = _state.update { it.copy(prompt = null) }

    fun onDonateClicked() = onDismiss()

    fun onOptOut() {
        onDismiss()
        viewModelScope.launch { supportRepository.optOut() }
    }
}
